/**
 * Routing constants for the application
 *
 * This file serves as the single source of truth for all navigation routes,
 * including admin routes with access control metadata.
 */

import { AdminAccessLevel, AdminNavItem, AdminNavSection, AdminRoutes } from '../types/admin/adminNavigation.types';
import { hasAccessToLevel } from '../utils/adminAccessUtils';
import { OTHER_EXTERNAL_LINKS, TICKET_LINKS } from './appConstants';

/**
 * External links for navigation - combines ticket links and other external links
 * @see appConstants.ts for the source of truth (TICKET_LINKS and OTHER_EXTERNAL_LINKS)
 */
export const EXTERNAL_LINKS = {
  ...OTHER_EXTERNAL_LINKS,
  ...TICKET_LINKS,
} as const;

// Main navigation items
export const NAV_ITEMS = {
  HOME: {
    text: 'Home',
    href: '/',
  },
  MANAGE_ATTRIBUTES: {
    text: 'Manage Attributes',
    href: '/manage-attributes',
  },
  SCENARIO_SNAPSHOTS: {
    text: 'Scenario Snapshots',
    href: '/scenario-snapshots',
  },
  FINANCE_CONTACTS: {
    text: 'Finance Contacts',
    href: '/finance-contacts',
  },
  VIEW_EDIT_PROGRAMS: {
    text: 'View/Edit Programs',
    href: '/manage-programs',
  },
  /**
   * Bulk Edit - A separate navigation item showing BulkEditHomePage
   * with Product Hierarchy table for bulk operations on multiple programs.
   *
   * Key behavior:
   * - Shows BulkEditHomePage directly (no redirect)
   * - No nested routes - item clicks navigate to View/Edit Programs (/manage-programs/*)
   * - Acts as a landing page for bulk selection/operations
   *
   * @see AppRoutes.tsx for route configuration
   * @see src/components/manage-programs/bulk-edit-product-hierarchy/ for components
   */
  BULK_EDIT: {
    text: 'Bulk Edit',
    href: '/bulk-edit',
  },
  PROGRAM_ROADMAP: {
    text: 'Program Roadmap',
    href: '/program-roadmap',
  },
  CHANGE_HISTORY: {
    text: 'Change History',
    href: '/change-history',
  },
};

/**
 * Admin routes with access control
 * Access level: ADMIN (isVerityAdmin OR isDeveloper)
 */
export const ADMIN_ROUTES: AdminRoutes = {
  ACCESS_MANAGEMENT: {
    text: 'Access Management',
    href: '/access-management',
    accessLevel: AdminAccessLevel.ADMIN,
  },
  ACCESS_MANAGEMENT_CREATE: {
    text: 'Create Access',
    href: '/access-management/create',
    accessLevel: AdminAccessLevel.ADMIN,
  },
  ACCESS_MANAGEMENT_EDIT: {
    text: 'Edit Access',
    href: '/access-management/edit',
    accessLevel: AdminAccessLevel.ADMIN,
  },
};

/**
 * Developer-only routes with access control
 * Access level: DEVELOPER (isDeveloper only)
 */
export const DEV_ROUTES: AdminRoutes = {
  REPORTS_CONFIGURATION: {
    text: 'Reports Configuration',
    href: '/reports-configuration',
    accessLevel: AdminAccessLevel.DEVELOPER,
  },
  REPORTS_CONFIGURATION_CREATE: {
    text: 'Create Report Configuration',
    href: '/reports-configuration/create',
    accessLevel: AdminAccessLevel.DEVELOPER,
  },
  REPORTS_CONFIGURATION_EDIT: {
    text: 'Edit Report Configuration',
    href: '/reports-configuration/edit',
    accessLevel: AdminAccessLevel.DEVELOPER,
  },
  PRODUCT_LINE_MANAGEMENT: {
    text: 'Product Line Management',
    href: '/product-line-management',
    accessLevel: AdminAccessLevel.DEVELOPER,
  },
  AUDIT_LOGS: {
    text: 'Audit Logs',
    href: '/audit-logs',
    accessLevel: AdminAccessLevel.DEVELOPER,
  },
  USER_METRICS: {
    text: 'User Metrics',
    href: '/user-metrics',
    accessLevel: AdminAccessLevel.DEVELOPER,
  },
};

/**
 * Dev navigation sections - Single source of truth
 * Used by AdminSideNavigation and TopNav menu dropdowns
 *
 * Each section has an access level that determines visibility:
 * - DEVELOPER: Visible to isDeveloper only
 */
export const DEV_NAV_SECTIONS: AdminNavSection[] = [
  {
    title: 'Dev Tools',
    accessLevel: DEV_ROUTES.REPORTS_CONFIGURATION.accessLevel,
    items: [
      {
        id: 'reports-configuration',
        text: DEV_ROUTES.REPORTS_CONFIGURATION.text,
        href: DEV_ROUTES.REPORTS_CONFIGURATION.href,
        accessLevel: DEV_ROUTES.REPORTS_CONFIGURATION.accessLevel,
      },
      {
        id: 'product-line-management',
        text: DEV_ROUTES.PRODUCT_LINE_MANAGEMENT.text,
        href: DEV_ROUTES.PRODUCT_LINE_MANAGEMENT.href,
        accessLevel: DEV_ROUTES.PRODUCT_LINE_MANAGEMENT.accessLevel,
      },
      {
        id: 'audit-logs',
        text: DEV_ROUTES.AUDIT_LOGS.text,
        href: DEV_ROUTES.AUDIT_LOGS.href,
        accessLevel: DEV_ROUTES.AUDIT_LOGS.accessLevel,
      },
      {
        id: 'user-metrics',
        text: DEV_ROUTES.USER_METRICS.text,
        href: DEV_ROUTES.USER_METRICS.href,
        accessLevel: DEV_ROUTES.USER_METRICS.accessLevel,
      },
    ],
  },
];

/**
 * Get filtered admin navigation sections based on user access
 *
 * Performs two levels of filtering:
 * 1. Filters out entire sections the user cannot access
 * 2. Filters out individual items within each section the user cannot access
 *
 * Note: A section may be included but have an empty items array if the user
 * has access to the section level but not to any individual items within it.
 *
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @param isInitiallyDeveloper - Whether user was originally a Developer (before DB override)
 * @returns Filtered navigation sections with filtered items the user has access to
 */
export const getFilteredAdminNavSections = (
  isVerityAdmin: boolean,
  isDeveloper: boolean,
  isInitiallyDeveloper: boolean = false
): AdminNavSection[] => {
  return DEV_NAV_SECTIONS.filter((section) =>
    hasAccessToLevel(section.accessLevel, isVerityAdmin, isDeveloper, isInitiallyDeveloper)
  ).map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      hasAccessToLevel(item.accessLevel, isVerityAdmin, isDeveloper, isInitiallyDeveloper)
    ),
  }));
};

/**
 * Get all visible admin navigation items as a flat list
 * Useful for TopNav dropdown menu
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @param isInitiallyDeveloper - Whether user was originally a Developer (before DB override)
 * @returns Flat list of navigation items the user has access to
 */
export const getAdminNavItems = (
  isVerityAdmin: boolean,
  isDeveloper: boolean,
  isInitiallyDeveloper: boolean = false
): AdminNavItem[] => {
  const filteredSections = getFilteredAdminNavSections(isVerityAdmin, isDeveloper, isInitiallyDeveloper);
  return filteredSections.flatMap((section) => section.items);
};

/**
 * Check if user has access to any admin route
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @param isInitiallyDeveloper - Whether user was originally a Developer (before DB override)
 * @returns Whether user can access at least one admin route
 */
export const hasAnyAdminAccess = (
  isVerityAdmin: boolean,
  isDeveloper: boolean,
  isInitiallyDeveloper: boolean = false
): boolean => {
  return isVerityAdmin || isDeveloper || isInitiallyDeveloper;
};

/**
 * Get the default admin route href for the user
 * Returns the first accessible admin route
 * @param isVerityAdmin - Whether user is a Verity Admin
 * @param isDeveloper - Whether user is a Developer
 * @param isInitiallyDeveloper - Whether user was originally a Developer (before DB override)
 * @returns Default admin route href or null if no access
 */
export const getDefaultAdminRoute = (
  isVerityAdmin: boolean,
  isDeveloper: boolean,
  isInitiallyDeveloper: boolean = false
): string | null => {
  const items = getAdminNavItems(isVerityAdmin, isDeveloper, isInitiallyDeveloper);
  return items.length > 0 ? items[0].href : null;
};

// Add more route sections here as needed
